import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { zodSupplierLetterMetaSchema } from '../validations';

import { type SupplierLetterMeta } from './types';

/**
 * Fetches the supplier authorization letter for a Company.
 * Returns `null` when no letter has been configured (404).
 *
 * @param companyId - The ID of the company.
 * @returns The supplier letter meta, or null when none is configured.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Supplier-Letter/operation/get_company_supplier_letter_vadmin_companies__company_id__supplier_letter_get
 */
export const fetchCompanySupplierLetter = async (
  companyId: UUID,
): Promise<SupplierLetterMeta | null> => {
  try {
    return await vestigasApi
      .get(ENDPOINT.COMPANY.SUPPLIER_LETTER.GET(companyId), {
        validate: { response: zodSupplierLetterMetaSchema },
      })
      .json<SupplierLetterMeta>();
  } catch (error) {
    if (error instanceof HTTPError && error.response.status === 404) {
      return null;
    }

    log.error({
      message: 'Error fetching company supplier letter',
      error,
    });

    ToastService.error(
      i18n.t(($) => $.company.fetchCompanySupplierLetter.error.generic, {
        ns: 'data',
      }),
    );

    throw error;
  }
};
